import React, { useMemo } from 'react';
import { Button, Progress, Tag } from 'antd';
import { useNavigate } from 'react-router-dom';
import { TCourse } from '../../models/courses/course';
import { useTranslation } from 'react-i18next';
import { getImagePath } from '../../utils/format/valueFormat';
import { PlayCircleOutlined } from '@ant-design/icons';
import './CourseCard.css';

interface CourseCardProps {
    course: TCourse;
    index?: number;
}

const gradientThemes = [
    {
        accent: '#2992BF',
        background: 'linear-gradient(135deg, rgba(41, 146, 191, 0.12), rgba(139, 190, 30, 0.12))',
    },
    {
        accent: '#8BBE1E',
        background: 'linear-gradient(135deg, rgba(139, 190, 30, 0.15), rgba(234, 164, 0, 0.12))',
    },
    {
        accent: '#EAA400',
        background: 'linear-gradient(135deg, rgba(234, 164, 0, 0.16), rgba(41, 146, 191, 0.12))',
    },
];

const CourseCard: React.FC<CourseCardProps> = ({ course, index = 0 }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const theme = gradientThemes[index % gradientThemes.length];

    const meta = useMemo(() => {
        const progression = Math.round(course.progression_state ?? 0);
        const minAge = course.min_age ?? undefined;
        const maxAge = course.max_age ?? undefined;
        const hasMin = typeof minAge === 'number';
        const hasMax = typeof maxAge === 'number';
        const ageLabel = hasMin || hasMax
            ? (hasMin && hasMax
                ? t('courses.ageRange', { min: minAge, max: maxAge })
                : hasMin
                    ? t('courses.ageMinimum', { min: minAge })
                    : t('courses.ageMaximum', { max: maxAge }))
            : t('courses.anyAge', 'All ages');

        return {
            progression,
            ageLabel,
        };
    }, [course, t]);

    const goToDetails = () => {
        navigate(`/course-details/${course.id}`);
    };

    return (
        <article
            className="course-card"
            style={{
                background: theme.background,
                borderColor: `${theme.accent}33`,
            }}
        >
            <div className="course-card__media">
                {course.subject_title && (
                    <span
                        className="course-card__subject"
                        style={{ background: theme.accent }}
                        title={course.subject_title}
                    >
                        {course.subject_title}
                    </span>
                )}
                <img
                    src={getImagePath(course.image)}
                    alt={course.title}
                    loading="lazy"
                />
                <div className="course-card__progress">
                    <Progress
                        type="circle"
                        percent={meta.progression}
                        strokeColor={{
                            '0%': theme.accent,
                            '100%': theme.accent,
                        }}
                        format={(percent) => `${percent}%`}
                        width={68}
                    />
                </div>
            </div>
            <div className="course-card__body">
                <div className="course-card__header">
                    <h3>{course.title}</h3>
                    <Tag color={theme.accent}>{meta.ageLabel}</Tag>
                </div>
                <p className="course-card__description">{course.description}</p>
            </div>
            <div className="course-card__footer">
                <Button
                    type="primary"
                    icon={<PlayCircleOutlined />}
                    onClick={goToDetails}
                >
                    {meta.progression >= 100
                        ? t('courses.card.revisit', 'Revisit course')
                        : t('courses.card.start', 'Continue')}
                </Button>
            </div>
        </article>
    );
};

export default CourseCard;
